
  import { computed, defineComponent, onMounted, ref, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRoute, useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import Editor from '@tinymce/tinymce-vue';
  import Multiselect from '@vueform/multiselect';
  import axios from 'axios';
  import moment from 'moment';

  export default defineComponent({
    name: 'blogs-creating',
    components: {
      editor: Editor,
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const onSubmitPullDataButton = ref<HTMLButtonElement | null>(null);
      const multiselect = ref<{ noOptions: boolean } | null>(null);
      const loading = ref(false);
      const thumbnail = ref('/media/avatars/blank-image.jpg');
      const authorAvatarThumbnail = ref('/media/avatars/blank-image.jpg');
      const isScheduled = ref(false);
      const addToTestimonials = ref(true);
      const allTags = computed(() => store.getters.allListBlogsTags || []);
      const allBlogs = computed(() => store.getters.getAllBlogsList || []);
      const allBlogCategories = computed(
        () => store.getters.allListBlogCategories || []
      );
      const blogData = ref({
        titleAr: '',
        titleEn: '',
        descriptionAr: '',
        descriptionEn: '',
        authorName: '',
        sourceName: '',
        sourceLink: '',
        externalReference: '',
        isExternal: false,
        status: '',
        image: null as File | null,
        authorAvatar: null as File | null,
        contentAr: '',
        contentEn: '',
        relatedIds: [] as number[],
        tagIds: [] as number[],
        publishedAt: '',
      });

      const handleImageUpload = (blobInfo, success, failure) => {
        const formData = new FormData();
        formData.append('media', blobInfo.blob(), blobInfo.filename());

        axios
          .post(`blogs/creation/uploadContentMedia`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then((response) => {
            const imageUrl = response.data?.data?.images;
            console.log(response.data.data);

            if (imageUrl) {
              success(imageUrl);
            } else {
              failure('Image URL not returned from server');
            }
          })
          .catch((error) => {
            console.error('Upload failed:', error);
            failure('Image upload failed: ' + error.message);
          });
      };

      const isArticlesCategory = (categoryId) =>
        allBlogCategories.value.some(
          (category) =>
            category.id === categoryId && category.slug === 'Articles'
        );

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const onSubmitPullData = async (values) => {
        if (onSubmitPullDataButton.value) {
          onSubmitPullDataButton.value!.disabled = true;
          onSubmitPullDataButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const data = await store.dispatch(Actions.PULL_URL_NEWS, {
          url: blogData.value.externalReference,
        });

        blogData.value.titleAr = data.title;
        blogData.value.titleEn = data.title;
        blogData.value.descriptionAr = data.description;
        blogData.value.descriptionEn = data.description;
        blogData.value.image = data.image;
        // Use the article's own publish date when the link has one
        // (`date` for al-Qabas links, `publishedAt` for the rest).
        const pulledDate = moment(data?.publishedAt || data?.date);
        if ((data?.publishedAt || data?.date) && pulledDate.isValid()) {
          blogData.value.publishedAt = pulledDate.format('YYYY-MM-DDTHH:mm');
        }
        thumbnail.value = data.image;

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          onSubmitPullDataButton.value?.removeAttribute('data-kt-indicator');
          onSubmitPullDataButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_PULLED_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            onSubmitPullDataButton.value?.removeAttribute('data-kt-indicator');
            onSubmitPullDataButton.value!.disabled = false;
          });
        }
      };

      const previewImage = (
        e: Event,
        type: 'cover' | 'authorAvatar' = 'cover'
      ) => {
        const input = e.target as HTMLInputElement;
        if (input.files && input.files[0]) {
          const file = input.files[0];
          const previewUrl = URL.createObjectURL(file);

          if (type === 'cover') {
            thumbnail.value = previewUrl;
            blogData.value.image = file;
          } else if (type === 'authorAvatar') {
            authorAvatarThumbnail.value = previewUrl;
            blogData.value.authorAvatar = file;
          }
        }
      };

      const blogSchema = Yup.object().shape({
        titleAr: Yup.string().required(translate('AR_TITLE_IS_REQUIRED')),
        titleEn: Yup.string().required(translate('EN_TITLE_IS_REQUIRED')),
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        try {
          const blogPayload = {
            ...values,
            externalReference: blogData.value.externalReference,
            status: isScheduled.value ? 'SCHEDULE' : 'PUBLISH',
            contentAr: blogData.value.contentAr,
            contentEn: blogData.value.contentEn,
            sourceName: blogData.value.sourceName,
            sourceLink: blogData.value.sourceLink,
            authorName: blogData.value.authorName,
            addToTestimonials: addToTestimonials.value,
          };

          delete blogPayload.image;
          delete blogPayload.authorAvatar;

          blogPayload.status = isScheduled.value ? 'SCHEDULE' : 'PUBLISH';

          if (
            typeof blogData.value.image === 'string' &&
            blogData.value.image
          ) {
            blogPayload.image = blogData.value.image;
          }

          // Empty means the API uses the time the blog is created.
          if (values.publishedAt) {
            blogPayload.publishedAt = moment(values.publishedAt).toISOString();
          } else {
            delete blogPayload.publishedAt;
          }

          if (values.scheduledAt) {
            blogPayload.scheduledAt = moment(values.scheduledAt).format(
              'YYYY-MM-DDTHH:mm'
            );
          }

          if (values.externalReference) {
            blogPayload.isExternal = true;
          } else {
            blogPayload.isExternal = false;
          }

          const result = await store.dispatch(Actions.CREATE_BLOG, blogPayload);

          if (values.image instanceof File) {
            const coverFormData = new FormData();
            coverFormData.append('image', values.image);

            await store.dispatch(Actions.UPLOAD_BLOG_COVER_IMAGE, {
              blogId: result.id,
              data: coverFormData,
            });
          }

          if (values.authorAvatar) {
            const authorAvatarFormData = new FormData();
            authorAvatarFormData.append('author', values.authorAvatar);

            await store.dispatch(Actions.UPLOAD_BLOG_AUTHOR_AVATAR, {
              blogId: result.id,
              data: authorAvatarFormData,
            });
          }

          Swal.fire({
            text: translate('SUCCESSFULLY_ADDED_BLOG'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'blogs-listing' });
          });
        } catch (error) {
          console.error('Blog creation failed:', error);

          const [errorName] = Object.keys(store.getters.getErrors);

          Swal.fire({
            text: translate(error?.[0] || 'CREATE_BLOG_FAILED'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          if (submitButton.value) {
            submitButton.value.disabled = false;
            submitButton.value.removeAttribute('data-kt-indicator');
          }
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('blogCreating'), [
          translate('blogs'),
        ]);
        await store.dispatch(Actions.GET_ALL_BLOGS);
        await store.dispatch(Actions.GET_ALL_BLOG_TAGS);
        await store.dispatch(Actions.GET_ALL_BLOG_CATEGORIES);
      });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitCreate,
        submitButton,
        translate,
        blogSchema,
        blogData,
        goBack,
        can,
        multiSelectLabel: 'title',
        loading,
        multiselect,
        previewImage,
        thumbnail,
        authorAvatarThumbnail,
        allTags,
        allBlogs,
        isScheduled,
        addToTestimonials,
        isArticlesCategory,
        handleImageUpload,
        allBlogCategories,
        onSubmitPullData,
        onSubmitPullDataButton,
      };
    },
  });
